import type { AxiosError } from 'axios';

import { CONFIG } from 'src/global-config';

import { AUTH } from './firebase';

// ----------------------------------------------------------------------
// Backend error reporter: batches repeated API failures, shows a user-facing
// banner, and posts to a make.com webhook for out-of-band alerting.
// Works for both authenticated and unauthenticated sessions.
// ----------------------------------------------------------------------

const WEBHOOK_URL = import.meta.env.VITE_ERROR_WEBHOOK_URL ?? '';

const THRESHOLD_COUNT = 3;
const THRESHOLD_WINDOW_MS = 60_000;
const REPORT_COOLDOWN_MS = 5 * 60_000;
const SESSION_KEY = 'artham_anon_session_id';

type FailureRecord = {
  at: number;
  url: string;
  method: string;
  status: number | 0;
  message: string;
};

type ReportPayload = {
  timestamp: string;
  appName: string;
  appVersion: string;
  serverUrl: string;
  session: {
    authenticated: boolean;
    uid?: string;
    email?: string | null;
    anonymousSessionId?: string;
  };
  device: {
    userAgent: string;
    online: boolean;
    language: string;
    platform: string;
  };
  failureCount: number;
  windowMs: number;
  failures: FailureRecord[];
};

const listeners = new Set<(evt: { count: number; lastFailure: FailureRecord }) => void>();
const failures: FailureRecord[] = [];
let lastReportAt = 0;

const isBrowser = typeof window !== 'undefined';

const getAnonSessionId = (): string => {
  if (!isBrowser) return 'ssr';
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `anon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const buildSession = (): ReportPayload['session'] => {
  const user = AUTH?.currentUser;
  if (user) {
    return { authenticated: true, uid: user.uid, email: user.email };
  }
  return { authenticated: false, anonymousSessionId: getAnonSessionId() };
};

const buildDevice = (): ReportPayload['device'] => ({
  userAgent: isBrowser ? navigator.userAgent : 'ssr',
  online: isBrowser ? navigator.onLine : true,
  language: isBrowser ? navigator.language : 'en',
  platform: isBrowser ? navigator.platform : 'ssr',
});

const postToWebhook = async (payload: object): Promise<void> => {
  if (!WEBHOOK_URL) return;
  try {
    await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[error-reporter] webhook post failed', err);
  }
};

// ----------------------------------------------------------------------
// Client/render error reporter: posts uncaught React render errors (caught by
// the route ErrorBoundary) to the same make.com webhook. De-duped + cooled
// down so a render-loop can't spam the webhook.
// ----------------------------------------------------------------------

let lastClientReportAt = 0;
let lastClientSignature = '';

export const reportClientError = (error: unknown, context?: { source?: string }): void => {
  const err = (error ?? {}) as Partial<Error>;
  const signature = `${err.name ?? ''}:${err.message ?? String(error)}`;
  const now = Date.now();

  // Skip identical errors fired within the cooldown window (e.g. render loops).
  if (signature === lastClientSignature && now - lastClientReportAt < REPORT_COOLDOWN_MS) {
    return;
  }
  lastClientReportAt = now;
  lastClientSignature = signature;

  void postToWebhook({
    type: 'client_render_error',
    timestamp: new Date(now).toISOString(),
    appName: CONFIG.appName,
    appVersion: CONFIG.appVersion,
    serverUrl: CONFIG.serverUrl,
    session: buildSession(),
    device: buildDevice(),
    error: {
      name: err.name,
      message: err.message ?? String(error),
      stack: err.stack,
      source: context?.source,
    },
  });
};

export const subscribeBackendErrors = (
  listener: (evt: { count: number; lastFailure: FailureRecord }) => void
) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const isReportableFailure = (error: AxiosError): boolean => {
  const status = error.response?.status ?? 0;
  if (status === 0) return true;
  if (status >= 500) return true;
  if (status === 408 || status === 429) return true;
  return false;
};

export const recordBackendFailure = (error: AxiosError): void => {
  if (!isReportableFailure(error)) return;

  const now = Date.now();
  const record: FailureRecord = {
    at: now,
    url: error.config?.url ?? 'unknown',
    method: (error.config?.method ?? 'get').toUpperCase(),
    status: error.response?.status ?? 0,
    message: error.message ?? 'Unknown error',
  };

  failures.push(record);
  while (failures.length > 0 && now - failures[0].at > THRESHOLD_WINDOW_MS) {
    failures.shift();
  }

  if (failures.length >= THRESHOLD_COUNT) {
    listeners.forEach((l) => {
      try {
        l({ count: failures.length, lastFailure: record });
      } catch (e) {
        console.warn('[error-reporter] listener error', e);
      }
    });

    if (now - lastReportAt > REPORT_COOLDOWN_MS) {
      lastReportAt = now;
      const payload: ReportPayload = {
        timestamp: new Date(now).toISOString(),
        appName: CONFIG.appName,
        appVersion: CONFIG.appVersion,
        serverUrl: CONFIG.serverUrl,
        session: buildSession(),
        device: buildDevice(),
        failureCount: failures.length,
        windowMs: THRESHOLD_WINDOW_MS,
        failures: failures.slice(-10),
      };
      void postToWebhook(payload);
    }
  }
};

export const resetBackendFailures = (): void => {
  failures.length = 0;
};
